// GRN phase 1 (2026-10-21, g02): Create PO asks how it was bought, every time, with nothing pre-selected.
// "Order from vendor" asks the expected delivery date (g11), filled from how long the vendor usually takes.
import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Drawer, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { getVendorLeadTime, LeadTime } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';

export type PurchasePath = 'direct' | 'order';

interface Props {
  open: boolean;
  vendorId: number | null;
  vendorName: string;
  /** e.g. "2 lines · 8,500.00 EGP" */
  summary: string;
  busy?: boolean;
  onCancel: () => void;
  onDirect: () => void;
  onOrder: (expectedDelivery: string) => void;
}

const MUTED = '#5f6672';

const PathChoice: React.FC<Props> = ({ open, vendorId, vendorName, summary, busy, onCancel, onDirect, onOrder }) => {
  const compact = useCompactLayout();
  const [path, setPath] = useState<PurchasePath | null>(null);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [lead, setLead] = useState<LeadTime | null>(null);

  useEffect(() => {
    if (!open) return;
    setPath(null); // g02: nothing pre-selected, every time
    setDate(null);
    setLead(null);
    if (vendorId) {
      getVendorLeadTime(vendorId)
        .then((lt) => { setLead(lt); setDate(dayjs().add(lt.days, 'day')); })
        .catch(() => setDate(dayjs().add(7, 'day')));
    } else {
      setDate(dayjs().add(7, 'day'));
    }
  }, [open, vendorId]);

  const leadText = !lead
    ? ''
    : lead.basis === 'history'
      ? `${vendorName} usually delivers in ${lead.days} day${lead.days === 1 ? '' : 's'} (median of its last ${lead.orders} vendor order${lead.orders === 1 ? '' : 's'}). Change it if they promised another date.`
      : lead.basis === 'vendor'
        ? `${vendorName}'s usual lead time is ${lead.days} day${lead.days === 1 ? '' : 's'}. Change it if they promised another date.`
        : `No past vendor orders from ${vendorName}, so 7 days by default. Change it if they promised another date.`;

  const option = (value: PurchasePath, title: string, text: string) => {
    const on = path === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        onClick={() => setPath(value)}
        style={{
          textAlign: 'left', border: on ? '2px solid #1677ff' : '1px solid #d9d9d9', background: on ? '#e6f4ff' : '#fff',
          borderRadius: 10, padding: on ? 11 : 12, display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', gap: 10,
          font: 'inherit', color: 'inherit', cursor: 'pointer', minHeight: 64, width: '100%',
        }}
      >
        <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', border: on ? '6px solid #1677ff' : '2px solid #bfbfbf', marginTop: 2 }} />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <b>{title}</b>
          <span style={{ fontSize: 13, color: MUTED }}>{text}</span>
        </span>
      </button>
    );
  };

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 13, color: MUTED }}>{vendorName} · {summary}</div>
      <div role="radiogroup" aria-label="How was it bought?" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {option('direct', 'Already bought · direct purchase', 'You paid and have the goods. Record the purchase and its receipt in one step.')}
        {option('order', 'Order from vendor · delivery later', 'The vendor delivers later. The PO waits in Expected deliveries until the goods arrive.')}
      </div>
      {path === 'order' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="po-expected" style={{ fontWeight: 600 }}><span style={{ color: '#cf1322' }}>*</span> Expected delivery</label>
          <DatePicker
            id="po-expected"
            value={date}
            onChange={setDate}
            format="D MMM YYYY"
            allowClear={false}
            disabledDate={(d) => d.isBefore(dayjs(), 'day')}
            style={{ width: compact ? '100%' : 220 }}
            inputReadOnly={compact}
          />
          {leadText && <span style={{ fontSize: 12, color: MUTED }}>{leadText}</span>}
        </div>
      )}
      {!path && <span style={{ fontSize: 13, color: MUTED }}>Nothing is chosen for you. Pick one to continue.</span>}
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
      <Button onClick={onCancel} style={compact ? { flex: 1, height: 44 } : undefined}>Cancel</Button>
      <Button
        type="primary"
        loading={busy}
        disabled={!path || (path === 'order' && !date)}
        onClick={() => (path === 'direct' ? onDirect() : onOrder(date!.format('YYYY-MM-DD')))}
        style={compact ? { flex: 1.4, height: 44 } : undefined}
      >
        {path === 'order' ? 'Create PO (Ordered)' : 'Continue'}
      </Button>
    </div>
  );

  if (compact) {
    return (
      <Drawer open={open} onClose={onCancel} placement="bottom" height="auto" title="How was it bought?" footer={footer} styles={{ body: { paddingBottom: 8 } }} destroyOnClose>
        {body}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onCancel} title="How was it bought?" footer={footer} width={520} destroyOnClose>
      {body}
    </Modal>
  );
};

export default PathChoice;
